import React, { useState } from "react";
import {
  Card,
  Typography,
  Avatar,
  Space,
  Input,
  Button,
  Upload,
  Image,
  Modal,
  Form,
  Divider,
  Select,
  message,
  Col,
  Tooltip,
  Tag,
  Descriptions,
} from "antd";
import {
  UserOutlined,
  RobotOutlined,
  ClockCircleOutlined,
  SendOutlined,
  PaperClipOutlined,
  SyncOutlined,
  LikeOutlined,
  DislikeOutlined,
  LikeFilled,
  DislikeFilled,
  DeleteOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  FileTextOutlined,
  HistoryOutlined,
  PhoneOutlined,
  NumberOutlined,
  TagsOutlined,
  MailOutlined,
  WarningOutlined,
  TagOutlined,
} from "@ant-design/icons";
import RenderImageCollage from "../../../pages/playground/sections/ChatWindowSection/ChatImage";
import { useAppDispatch } from "../../../hooks/reduxHooks";
import {
  handleFeedbackApi,
  handleFeedbackDeleteApi,
} from "../../../services/feedbackLog.service";
import FeedbackModal from "../../../pages/playground/sections/ChatWindowSection/FeedbackModal";
import { resolveCtaApi } from "../../../services/channels.service";
import { transformString } from "../../../helpers/channelNameTransformation";
import { getCtaByIdApi } from "../../../services/cta.service";
// import "./index.css";
import { formatDateTime } from "../../../utils/formatDateTime";
import { filterValidUrls } from "../../../utils/urlValidator";
import QueryFindingInfoButton from "../../QueryFindingInfoButton";

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

// Format the timestamp
const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// Parse reply text with line breaks
const parseReplyText = (text) => {
  return text.split("\n").map((line, index) => (
    <span key={index}>
      {line}
      {index < text.split("\n").length - 1 && <br />}
    </span>
  ));
};

const SingleChatItemChannel = ({
  message: msng,
  convIndex,
  msgIndex,
  setVisible,
  setResponseData,
  setDrawerData,
  customerInfo,
  fetchChatHis,
  setRefreshCustomerList,
}) => {
  const dispatch = useAppDispatch();

  // State for resolve modal
  const [resolveModalVisible, setResolveModalVisible] = useState(false);
  const [resolveRemarks, setResolveRemarks] = useState("");
  const [resolveFormSubmitting, setResolveFormSubmitting] = useState(false);
  const [ctaByIdInfo, setCTAByIdInfo] = useState();
  const [resolveForm] = Form.useForm();

  // State for like/dislike buttons - initialize based on evaluation_status
  const [liked, setLiked] = useState(msng?.evaluation_status === "like");
  const [disliked, setDisliked] = useState(
    msng?.evaluation_status === "dislike"
  );
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);

  // Extract images from message if they exist
  const images = msng.images || [];
  const hasMedia = msng?.media_ids && msng?.media_ids?.length > 0;

  // State for feedback modal
  const [form] = Form.useForm();

  // Split message content by double newlines for assistant messages
  const messageParts =
    msng?.role === "assistant" && msng?.content
      ? msng?.content
        .replace(/\\n/g, "") // Unescape newlines
        .replace(/\\n{2}/g, " \n") // Exactly 2 newlines → space
        .split(/\n{3,}/) // Split on 3+ newlines
        .filter((part) => part.trim())
      : [];
  const aiActionsCount = Array.isArray(msng?.information_gathering)
    ? msng.information_gathering.length
    : 0;
  const hasAiActions = aiActionsCount > 0;
  const queryFinding = msng?.query_finding || msng?.query_findings;
  const normalizeCtaType = (type) =>
    String(type || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");
  const isEndConversationType =
    normalizeCtaType(ctaByIdInfo?.type || msng?.cta?.[0]?.issue_type) ===
    "end_conversation";
  const getResolveErrorMessage = (error) =>
    error?.response?.data?.detail ||
    error?.message ||
    "Failed to resolve CTA. Please try again.";

  // Handle like button click
  const handleLike = (e) => {
    e.stopPropagation(); // Prevent bubble click event

    // If already liked, undo the like
    if (liked) {
      // Add API call to remove the like
      dispatch(
        handleFeedbackDeleteApi({
          data: {
            message_id: msng?.message_id,
          },
          finalCallback: () => { },
          successCallback: (response) => {
            setLiked(false);
            message.warning("Unliked!");
          },
          failureCallback: () => { },
        })
      );
      return;
    }

    dispatch(
      handleFeedbackApi({
        data: {
          message_id: msng?.message_id,
          evaluation: "like",
          remark: null,
          categories: null,
          reviewer_id: localStorage.getItem("userId"),
          created_at: null,
        },
        finalCallback: () => { },
        successCallback: (response) => {
          // Otherwise, set liked and remove disliked if necessary
          setLiked(true);
          // After dislike if user likes it dislike is removed
          if (disliked) setDisliked(false);

          // console.log("RESPONSE -> ", response);
          message.success("Liked!");
        },
        failureCallback: () => { },
      })
    );
  };

  // Handle dislike button click
  const handleDislike = (e) => {
    e.stopPropagation(); // Prevent bubble click event

    // If already disliked, just undo it without showing modal
    if (disliked) {
      dispatch(
        handleFeedbackDeleteApi({
          data: {
            message_id: msng?.message_id,
          },
          finalCallback: () => { },
          successCallback: (response) => {
            setDisliked(false);
            message.warning("Dislike removed!");
          },
          failureCallback: () => { },
        })
      );

      return;
    }

    setFeedbackModalVisible(true);
  };

  const fetchCTAById = (id) => {
    dispatch(
      getCtaByIdApi({
        id: id,
        finalCallback: () => { },
        successCallback: (response) => {
          console.log("RESPONSE of fetchCTAById-> ", response);
          setCTAByIdInfo(response?.data);
          // setResponseData(response?.data);
        },
        failureCallback: () => { },
      })
    );
  };

  // Function to generate consistent colors based on names
  const getNameColor = (name) => {
    const colors = [
      "#f56a00",
      "#7265e6",
      "#ffbf00",
      "#00a2ae",
      "#1890ff",
      "#52c41a",
      "#722ed1",
      "#eb2f96",
    ];

    // Simple hash function to get consistent color for a name
    let hash = 0;
    if (!name) return colors[0];
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div
      key={`conv-${convIndex}-msg-${msgIndex}`}
      style={{
        marginBottom: "16px",
      }}
    >
      {msng?.role === "user" ? (
        // USER MESSAGES - Left Side
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start", // Left aligned for user
            }}
          >
            <Avatar
              style={{
                backgroundColor: getNameColor(customerInfo?.customer_name),
                marginRight: "8px",
                verticalAlign: 'middle',
                flexShrink: 0,
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              {customerInfo?.customer_name
                ? customerInfo.customer_name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2)
                : <UserOutlined />}
            </Avatar>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              {msng?.content ? (
                <div
                  style={{
                    display: "flex",
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "#f0f0f0", // Light gray for user
                      borderRadius: "8px",
                      padding: "12px",
                      maxWidth: "600px",
                      display: "flex",
                      flexDirection: "column",
                      gap: msng?.ads_flag && msng?.simulated_automatic_message ? "8px" : "0px"
                    }}
                  >
                    {msng?.ads_flag && msng?.simulated_automatic_message && (
                      <div
                        style={{
                          backgroundColor: "rgba(0, 0, 0, 0.06)",
                          borderLeft: "4px solid #2e8b57",
                          padding: "6px 10px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          lineHeight: "1.4",
                          maxWidth: "100%",
                          textAlign: "left"
                        }}
                      >
                        <div style={{ fontWeight: 600, color: "#2e8b57", marginBottom: "2px", display: "flex", alignItems: "center", gap: "4px" }}>
                          <iconify-icon icon="mdi:bullhorn-outline" width="14" height="14"></iconify-icon>
                          Replied to Ad
                        </div>
                        <div style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color: "#555"
                        }}>
                          {msng.simulated_automatic_message}
                        </div>
                      </div>
                    )}
                    <Paragraph style={{ marginBottom: 0 }}>
                      {msng?.content}
                    </Paragraph>
                  </div>
                  {!msng?.ai_enabled || !msng?.has_credit ? (
                    <div
                      style={{
                        marginTop: "0px",
                        marginLeft: "6px",
                      }}
                    >
                      <Tooltip
                        placement="right"
                        title={
                          <p
                            style={{
                              fontSize: "11px",
                              margin: "0",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: 500,
                                fontSize: "13px",
                              }}
                            >
                              No AI Reply for this message!
                            </span>
                            <br />
                            {!msng?.has_credit && !msng?.ai_enabled
                              ? "AI Assistant was disabled when message was received and please recharge your account to continue receiving AI Reply."
                              : !msng?.has_credit
                                ? "No credits left please recharge your account to continue receiving AI Reply."
                                : !msng?.ai_enabled
                                  ? "AI Assistant was disabled when message was received."
                                  : ""}
                          </p>
                        }
                      >
                        <WarningOutlined
                          style={{ fontSize: "16px", color: "#ff4d4f" }}
                        />
                        {/* <Text type="danger">
                          <iconify-icon
                            icon="cuida:warning-outline"
                            width="24"
                            height="24"
                          ></iconify-icon>
                        </Text> */}
                      </Tooltip>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              ) : !hasMedia && !msng?.message_id ? (
                <div style={{ marginBottom: "16px" }}>
                  <div
                    className="message-loading"
                    style={{
                      backgroundColor: "rgb(220, 245, 200)",
                    }}
                  >
                    <div className="bouncing-dots">
                      <span
                        style={{ backgroundColor: "rgb(46, 139, 87)" }}
                      ></span>
                      <span
                        style={{ backgroundColor: "rgb(46, 139, 87)" }}
                      ></span>
                      <span
                        style={{ backgroundColor: "rgb(46, 139, 87)" }}
                      ></span>
                    </div>
                  </div>
                </div>
              ) : hasMedia ? (
                /* Render media inline when there's no content */
                <div style={{ maxWidth: "80%" }}>
                  {typeof msng.media_ids === "string" ? (
                    <Card style={{ backgroundColor: "#f0f0f0" }}>
                      {msng?.media_ids}
                    </Card>
                  ) : (
                    <RenderImageCollage images={msng?.media_ids} />
                  )}
                </div>
              ) : null}

              {/* Render media below when there's also content */}
              {hasMedia && msng?.content && (
                <div style={{ marginTop: "8px", maxWidth: "80%" }}>
                  {typeof msng.media_ids === "string" ? (
                    <Card style={{ backgroundColor: "#f0f0f0" }}>
                      {msng?.media_ids}
                    </Card>
                  ) : (
                    <RenderImageCollage images={msng?.media_ids} />
                  )}
                </div>
              )}
            </div>
          </div>
          <div
            style={{
              marginLeft: "40px",
              marginTop: "4px",
            }}
          >
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {formatTime(msng?.created_at || new Date().toISOString())}
            </Text>
          </div>
        </div>
      ) : (
        // ASSISTANT MESSAGES - Right Side
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-start",
              width: "100%",
              gap: "8px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                maxWidth: "calc(100vw - 144px)",
              }}
            >
              {/* Map through message parts to create multiple bubbles */}
              {messageParts?.length > 0 ? (
                messageParts?.map((part, partIndex) => {
                  return (
                    <div
                      style={{
                        display: "flex",
                        gap: "6px",
                        marginBottom: "8px",
                      }}
                    >
                      {msng?.cta?.[0]?.cta_status === "open" ? (
                        <div style={{ marginTop: "0px" }}>
                          <Tooltip title="Resolve">
                            <Button
                              type="primary"
                              size="small"
                              icon={<ExclamationCircleOutlined />}
                              style={{
                                backgroundColor: "#ffc069",
                                alignSelf: "flex-start",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                fetchCTAById(msng?.cta?.[0]?.id);
                                setResolveModalVisible(true);
                              }}
                            />
                          </Tooltip>
                        </div>
                      ) : (
                        msng?.cta?.[0]?.cta_status === "resolved" && (
                          <div style={{ marginTop: "6px" }}>
                            <Tooltip
                              title={`${transformString(
                                msng?.cta?.[0]?.issue_type
                              )} has been resolved`}
                            >
                              <CheckCircleOutlined
                                style={{ fontSize: "18px", color: "#52c41a" }}
                              />
                            </Tooltip>
                          </div>
                        )
                      )}

                      <div
                        key={partIndex}
                        style={{
                          backgroundColor: "#e6f7ff", // Light blue for assistant
                          borderRadius: "8px",
                          padding: "12px",
                          cursor: "pointer",
                          alignSelf: "flex-end", // Align to right
                          maxWidth: "600px",
                        }}
                        onClick={() => {
                          setDrawerData(msng);
                          setVisible(true);
                        }}
                      >
                        <Paragraph style={{ marginBottom: 0 }}>
                          {parseReplyText(part)}
                        </Paragraph>
                      </div>
                    </div>
                  );
                })
              ) : (
                // Fallback for empty content or no double newlines
                <div
                  style={{
                    backgroundColor: "#e6f7ff", // Light blue for assistant
                    borderRadius: "8px",
                    padding: "12px",
                    minHeight: "46px",
                    cursor: "pointer",
                    alignSelf: "flex-end", // Align to right
                    maxWidth: "600px",
                  }}
                  onClick={() => {
                    setDrawerData(msng);
                    setVisible(true);
                    setResponseData(msng);
                  }}
                >
                  <Paragraph style={{ marginBottom: 0 }}>
                    {msng?.content ? (
                      parseReplyText(msng?.content)
                    ) : (
                      <span style={{ visibility: "hidden" }}>.</span>
                    )}
                  </Paragraph>
                </div>
              )}

              {msng?.reply_urls?.length > 0 && (() => {
                const validUrls = filterValidUrls(msng?.reply_urls);
                if (validUrls.length === 0) return null;

                return (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      marginBottom: "8px",
                      marginTop: "8px",
                    }}
                  >
                    {/* Group all images together for gallery preview */}
                    <Image.PreviewGroup>
                      {validUrls.map((resource, index) => {
                        // Function to determine if the resource is a YouTube video
                        const isYouTubeVideo = (url) => {
                          return (
                            url.includes("youtube.com") ||
                            url.includes("youtu.be")
                          );
                        };

                        // Function to extract video ID from YouTube URL
                        const extractVideoId = (youtubeUrl) => {
                          const standardMatch = youtubeUrl.match(
                            /youtube\.com\/watch\?v=([^&]+)/
                          );
                          const shortMatch =
                            youtubeUrl.match(/youtu\.be\/([^?]+)/);
                          const embedMatch = youtubeUrl.match(
                            /youtube\.com\/embed\/([^?]+)/
                          );

                          if (standardMatch) return standardMatch[1];
                          else if (shortMatch) return shortMatch[1];
                          else if (embedMatch) return embedMatch[1];
                          else return null;
                        };

                        // Function to check if URL is an image based on extension
                        const isImageUrl = (url) => {
                          const imageExtensions = [
                            "jpg",
                            "jpeg",
                            "png",
                            "gif",
                            "webp",
                            "svg",
                            "bmp",
                          ];
                          const extension = url
                            .split(".")
                            .pop()
                            .split(/[#?]/)[0]
                            .toLowerCase();
                          return imageExtensions.includes(extension);
                        };

                        // Determine if this is a YouTube video
                        const isYouTube = isYouTubeVideo(resource);
                        // Determine if this is an image
                        const isImage = isImageUrl(resource);

                        // Set appropriate display based on resource type
                        let thumbnail;
                        let playIconOverlay = null;

                        if (isYouTube) {
                          const videoId = extractVideoId(resource);
                          thumbnail = `https://img.youtube.com/vi/${videoId}/sddefault.jpg`;

                          // Add play button overlay for videos
                          playIconOverlay = (
                            <div
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                pointerEvents: "none", // This allows clicks to pass through to the image
                              }}
                            >
                              <div
                                style={{
                                  width: "30px",
                                  height: "30px",
                                  borderRadius: "50%",
                                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                {/* Triangle Play Icon */}
                                <div
                                  style={{
                                    width: 0,
                                    height: 0,
                                    borderTop: "7px solid transparent",
                                    borderBottom: "7px solid transparent",
                                    borderLeft: "12px solid white",
                                    marginLeft: "3px", // Slight offset to center visually
                                  }}
                                />
                              </div>
                            </div>
                          );
                        } else if (isImage) {
                          // For images, use the image URL itself as the thumbnail
                          thumbnail = resource;
                        } else {
                          // For other video types or unknown formats, use a generic thumbnail
                          thumbnail =
                            "https://placehold.co/120x90?text=Media";

                          // Add play button for non-YouTube videos
                          if (!isImage) {
                            playIconOverlay = (
                              <div
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  pointerEvents: "none",
                                }}
                              >
                                <div
                                  style={{
                                    width: "30px",
                                    height: "30px",
                                    borderRadius: "50%",
                                    backgroundColor: "rgba(0, 0, 0, 0.6)",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: 0,
                                      height: 0,
                                      borderTop: "7px solid transparent",
                                      borderBottom: "7px solid transparent",
                                      borderLeft: "12px solid white",
                                      marginLeft: "3px",
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          }
                        }

                        return (
                          <div
                            key={index}
                            style={{
                              width: "120px",
                              height: "90px",
                              borderRadius: "8px",
                              overflow: "hidden",
                              position: "relative",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                              border: "solid 1px #e0e0e0",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                              cursor: "pointer",
                            }}
                          >
                            {isImage ? (
                              // Use Ant Design's Image component for images
                              <Image
                                src={thumbnail}
                                alt="Image"
                                style={{
                                  objectFit: "cover",
                                  width: "100%",
                                  height: "100%",
                                }}
                                preview={{
                                  mask: null, // Remove default preview mask
                                }}
                                wrapperStyle={{
                                  width: "100%",
                                  height: "100%",
                                }}
                                fallback="https://via.placeholder.com/120x90?text=Image+Not+Found"
                              />
                            ) : (
                              // For videos, use regular img tag with click handler
                              <>
                                <img
                                  src={thumbnail}
                                  alt={isYouTube ? "YouTube video" : "Media"}
                                  style={{
                                    cursor: "pointer",
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                  onClick={() => window.open(resource, "_blank")}
                                  onError={(e) => {
                                    e.target.src = "https://via.placeholder.com/120x90?text=Media+Unavailable";
                                  }}
                                />
                                {playIconOverlay}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </Image.PreviewGroup>
                  </div>
                );
              })()}

            </div>
            <Avatar
              size="default"
              icon={<RobotOutlined />}
              style={{
                backgroundColor: "#1890ff",
                flexShrink: 0,
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "8px",
              marginTop: "4px",
              marginRight: "40px",
              marginBottom: "24px",
            }}
          >
            {hasAiActions && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  padding: "2px 8px",
                  borderRadius: "999px",
                  backgroundColor: "#e6f4ff",
                  border: "1px solid #91caff",
                  color: "#0958d9",
                  fontSize: "11px",
                  fontWeight: 500,
                  lineHeight: 1,
                }}
              >
                <iconify-icon
                  icon="mdi:robot-outline"
                  width="12"
                  height="12"
                  style={{ color: "#1677ff", display: "block", flexShrink: 0 }}
                ></iconify-icon>
                {`${aiActionsCount} AI action${aiActionsCount > 1 ? "s" : ""}`}
              </span>
            )}
            <QueryFindingInfoButton queryFinding={queryFinding} />
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {formatTime(msng?.created_at || new Date().toISOString())}
              {/* <Tooltip title={`It took ${(msng?.processing_time || 0).toFixed(2)} seconds to generate this response.`}>
                {` · ${(msng?.processing_time || 0).toFixed(2)}s`}
              </Tooltip> */}
            </Text>
            <Space style={{ marginLeft: "8px" }}>
              <Tooltip title="Like">
                <Button
                  type={liked ? "primary" : "text"}
                  size="small"
                  icon={liked ? <LikeFilled /> : <LikeOutlined />}
                  onClick={handleLike}
                />
              </Tooltip>

              <Tooltip title="Dislike">
                <Button
                  type={disliked ? "primary" : "text"}
                  size="small"
                  danger={disliked}
                  icon={disliked ? <DislikeFilled /> : <DislikeOutlined />}
                  onClick={handleDislike}
                />
              </Tooltip>
            </Space>
          </div>
        </div>
      )}

      <FeedbackModal
        message={msng}
        feedbackModalVisible={feedbackModalVisible}
        setFeedbackModalVisible={setFeedbackModalVisible}
        liked={liked}
        setLiked={setLiked}
        setDisliked={setDisliked}
      />

      <Modal
        title="Mark As Resolved"
        open={resolveModalVisible}
        onOk={() => resolveForm.submit()}
        width={700}
        onCancel={() => {
          setResolveModalVisible(false);
          resolveForm.resetFields();
        }}
        okText="Resolve"
        cancelText="Cancel"
        okButtonProps={{
          style: {
            backgroundColor: "#3D90D7",
            borderColor: "#3D90D7",
            color: "#fff",
            zIndex: "10001",
          },
        }}
      >
        <Form
          form={resolveForm}
          layout="vertical"
          onFinish={(values) => {
            const trimmedRemarks = values?.remarks?.trim?.() || "";
            dispatch(
              resolveCtaApi({
                data: {
                  cta_type: msng?.cta?.[0]?.issue_type,
                  id: msng?.cta?.[0]?.id,
                  resolved_by: localStorage.getItem("username"),
                  resolved_at: new Date().toISOString(),
                  remarks: trimmedRemarks,
                },
                finalCallback: () => {
                  setResolveFormSubmitting(false); // modal button loading
                },
                successCallback: () => {
                  message.success("CTA resolved successfully!");
                  setResolveModalVisible(false);
                  // Reset form
                  resolveForm.resetFields();
                  // Refresh Chat
                  fetchChatHis();
                  setRefreshCustomerList(true);
                },
                failureCallback: (error) => {
                  message.error(getResolveErrorMessage(error));
                },
              })
            );
          }}
        >
          <div>
            <Descriptions
              bordered
              column={1}
              labelStyle={{ width: "30%", fontWeight: 500 }}
              contentStyle={{ width: "70%" }}
            >
              <Descriptions.Item label="Customer Name">
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <Avatar icon={<UserOutlined />} />
                  <Text strong>
                    {transformString(ctaByIdInfo?.customer_name) || "N/A"}
                  </Text>
                </div>
              </Descriptions.Item>
              {ctaByIdInfo?.customer_phone_number || ctaByIdInfo?.extra_info?.["Contact Number"] ? (
                <Descriptions.Item label="Contact Number">
                  <Text>
                    <PhoneOutlined style={{ marginRight: "8px" }} />
                    {ctaByIdInfo?.customer_phone_number || ctaByIdInfo?.extra_info?.["Contact Number"] || "N/A"}
                  </Text>
                </Descriptions.Item>
              ) : null}
              {ctaByIdInfo?.customer_email ? (
                <Descriptions.Item label="Email Address">
                  <Text>
                    <MailOutlined style={{ marginRight: "8px" }} />
                    {ctaByIdInfo?.customer_email || "N/A"}
                  </Text>
                </Descriptions.Item>
              ) : null}
              <Descriptions.Item label="Description">
                <span dangerouslySetInnerHTML={{
                  __html: (ctaByIdInfo?.description || "No description provided")
                    .replace(/\n+/g, '<br>')
                    .replace(/\((\d{4}-\d{2}-\d{2}:\d{2}:\d{2}:\d{2})\)/g, (match, dateStr) => {
                      const formattedDate = formatDateTime({ date: dateStr.replace(':', 'T'), withTime: true });
                      return '<b>(' + formattedDate + ')</b>';
                    })
                }} />
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                <Tag icon={<TagOutlined />}>{transformString(ctaByIdInfo?.type) || "N/A"}</Tag>
              </Descriptions.Item>
            </Descriptions>
          </div>
          <Form.Item
            name="remarks"
            label={
              <Text
                strong
                style={{
                  fontSize: "16px",
                  marginTop: "20px",
                  display: "block",
                  fontWeight: 500,
                }}
              >
                Remarks{" "}
                {!isEndConversationType ? (
                  "(Optional)"
                ) : (
                  <span style={{ color: "red" }}>*</span>
                )}
              </Text>
            }
            rules={[
              {
                validator: (_, value) => {
                  if (!isEndConversationType) return Promise.resolve();
                  if (value?.trim()) return Promise.resolve();
                  return Promise.reject(
                    new Error("Please provide remarks to resolve this ticket.")
                  );
                },
              },
            ]}
          >
            <TextArea
              placeholder="Enter remarks for resolving the ticket."
              autoSize={{ minRows: 4, maxRows: 6 }}
              style={{ borderRadius: "4px" }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SingleChatItemChannel;
